<script lang="ts">
import { useRAF } from '@monogrid/vue-boost/core'
import { templateRef, useDevicesList, useUserMedia } from '@vueuse/core'
import { computed, defineComponent, onMounted, onUnmounted, provide, ref, watch } from 'vue'

import { useBreakpoint } from '@/composables/useBreakpoint'
import { useStoreWebcam } from '@/composables/useStoreWebcam'
import fxMobile from '@/data/fx-data-mobile.json'
import fxTablet from '@/data/fx-data-tablet.json'
import { getAssetURL } from '@/utils'
import { stopCameraStream } from '@/utils/stopCameraStream'
import { useOGL } from '@/webgl/composables/useGLFilter'
import { useStoreFX } from '@/webgl/composables/useStoreFX'
import { useStoreSnapshot } from '@/webgl/composables/useStoreSnapshot'
import { useBodySegmentation } from '@/webgl/utils/body-segmentation/useBodySegmentation'
import { clearCanvas } from '@/webgl/utils/clearCanvas'
import { handleContextLoss } from '@/webgl/utils/handleContextLoss'

type PermissionStatus = 'granted' | 'denied' | 'prompt'

const PERMISSION_STATUS = {
  GRANTED: 'granted',
  DENIED: 'denied',
  PROMPT: 'prompt'
} as const

export default defineComponent({
  name: 'GLFilter',
  components: { },
  props: {
    isCameraEnabled: { type: Boolean, required: true }
  },
  setup (props) {
    const glContainerEl = templateRef<HTMLElement | null>('glContainerEl')
    const mainContextEl = templateRef<HTMLCanvasElement | null>('mainContextEl')

    const glSegmentationEl = templateRef<HTMLCanvasElement | null>('glSegmentationEl')
    const glSegmentationMaskEl = templateRef<HTMLCanvasElement | null>('glSegmentationMaskEl')

    const backgroundLoopEl = templateRef<HTMLVideoElement | null>('backgroundLoopEl')
    const bubblesLoopEl = templateRef<HTMLVideoElement | null>('bubblesLoopEl')
    const leafLoopEl = templateRef<HTMLVideoElement | null>('leafLoopEl')

    // Webcamera initialization
    const cameraEl = templateRef<HTMLVideoElement | null>('cameraEl')
    const currentCamera = ref<string>()
    const webcamSize = ref({ width: 128, height: 128 }) // Set initial size to prevent WebGL:INVALID_VALUE

    const { FXIndex, updateFXIndex } = useStoreFX()

    const { videoInputs: cameras } = useDevicesList({
      requestPermissions: true,
      onUpdated: () => {
        if (cameras.value.find(i => i.deviceId === currentCamera.value)) {
          currentCamera.value = cameras.value[0]?.deviceId
        }
      }
    })

    const { stream, enabled } = useUserMedia({
      constraints: { video: { deviceId: currentCamera.value, facingMode: 'user' }, audio: false }
    })

    const { time, activate } = useRAF()
    const { snapped, enableButton } = useStoreSnapshot()

    // Screen size constraints
    const screen = useBreakpoint()
    const dataSrc = computed(() => !screen.sm ? fxMobile : fxTablet)

    // Browser permissions
    const permission = ref<PermissionStatus>(PERMISSION_STATUS.PROMPT)
    const { webcamState, setGlobalPermissions } = useStoreWebcam()

    const isLoading = ref(true)

    // Body segmentation using TensorFlow
    const { createSegmenter, segmenter, createSegmentation } = useBodySegmentation(glSegmentationEl, glSegmentationMaskEl)
    const { init: initOGL, updateImageResolution, snapFinalContent, segmentProgram: material, mainProgram: mainMaterial, fxOutput: fxMaterial } = useOGL()

    const handlePermit = async () => {
      await navigator.mediaDevices.getUserMedia({ video: true })
        .then(() => (permission.value = PERMISSION_STATUS.GRANTED))
        .catch(() => (permission.value = PERMISSION_STATUS.DENIED))
    }

    const loadedMetadataHandler = () => {
      activate()
      webcamSize.value = { width: cameraEl.value?.videoWidth, height: cameraEl.value?.videoHeight }
      isLoading.value = false
      enableButton(true)
    }

    watch(mainContextEl, (_ctn) => {
      if (!_ctn) return
      _ctn.addEventListener('webglcontextlost', e => handleContextLoss(e, _ctn))
    }, { immediate: true })

    watch([glContainerEl, mainContextEl, glSegmentationEl, glSegmentationMaskEl, backgroundLoopEl, bubblesLoopEl, leafLoopEl, enabled], ([section, filter, segment, alpha, background, fx1, fx2]) => {
      if (!section || !filter || !segment || !alpha || !background || !fx1 || !fx2) return
      initOGL(section, filter, segment, alpha, background, fx1, fx2)
    }, { immediate: true })

    watch(time, async () => {
      if (!segmenter.value || !cameraEl.value || !glSegmentationEl.value || !glSegmentationMaskEl.value || !enabled
      ) return
      // @ts-expect-error
      createSegmentation(cameraEl.value)
    })

    watch(([time, material, webcamSize]), ([_time, _material, _size]) => {
      if (!_material || !_size) return
      updateImageResolution(_size.width, _size.height)
    }, { immediate: true })

    watch([permission], ([permit]) => {
      if ((permit === PERMISSION_STATUS.GRANTED)) {
        createSegmenter()
      }

      setGlobalPermissions(permit)
    })

    watch(() => props.isCameraEnabled, async (_enable) => {
      enabled.value = _enable
      if (_enable) {
        handlePermit()

        // Wait for camera permission and stream to be ready
        await new Promise(resolve => {
          const permissionCheck = setInterval(() => {
            if (permission.value === PERMISSION_STATUS.GRANTED && stream.value) {
              clearInterval(permissionCheck)
              // @ts-expect-error
              resolve()
            }
          }, 100)
        })

        // Initialize the segmenter if permission is granted and stream is available
        if (permission.value === PERMISSION_STATUS.GRANTED && stream.value) {
          createSegmenter() // Initialize the segmenter
        }
      }
      if (!enabled.value) {
        // Cleaning when camera is disabled
        segmenter.value = null
        clearCanvas(glSegmentationEl.value)
        clearCanvas(glSegmentationMaskEl.value)
        stopCameraStream(stream.value)
      }
    })

    watch(cameraEl, (_video) => {
      if (_video) {
        cameraEl.value.srcObject = stream.value!
      } else {
        stopCameraStream(stream.value)
      }
    })

    watch([FXIndex, mainMaterial, fxMaterial], ([_fx, _mat, _fbo]) => {
      if (!_fx || !_mat || !_fbo) return

      const [uBubble, uLeaf] = [_mat.uniforms.uBubble, _mat.uniforms.uLeaf]
      const [uFboBubble, uFboLeaf] = [_fbo.uniforms.uBubble, _fbo.uniforms.uLeaf]

      const fxUniforms = [uBubble, uLeaf]
      const fxFboUniforms = [uFboBubble, uFboLeaf]

      for (let i = 0; i < fxUniforms.length; i++) {
        if (!snapped.value) {
          fxUniforms[i].value = i + 1 === _fx ? 1.0 : 0.0
          fxFboUniforms[i].value = i + 1 === _fx ? 1.0 : 0.0
        } else {
          updateFXIndex(0)
          fxUniforms[i].value = 0.0
          fxFboUniforms[i].value = 0.0
        }
      }

      if (_fx === 1) {
        bubblesLoopEl.value.play()
        leafLoopEl.value.pause()
      } else if (_fx === 2) {
        leafLoopEl.value.play()
        bubblesLoopEl.value.pause()
      } else {
        leafLoopEl.value.pause()
        bubblesLoopEl.value.pause()
      }
    }, { immediate: true, deep: true })

    onMounted(() => {
      enabled.value = props.isCameraEnabled

      handlePermit()

      cameraEl.value.addEventListener('loadedmetadata', loadedMetadataHandler)
    })

    onUnmounted(() => {
      stopCameraStream(stream.value)
      cameraEl.value.removeEventListener('loadedmetadata', loadedMetadataHandler)
    })

    provide('takeSnap', snapFinalContent)

    return {
      permission,
      enabled,
      isLoading,
      stream,
      PERMISSION_STATUS,
      webcamSize,
      getAssetURL,
      snapped,
      dataSrc,
      webcamState
    }
  }
})
</script>

<template>
  <section id="section" ref="sectionEl" class="ctn-gl absolute inset-0 w-full h-full overflow-hidden">
    <div ref="glContainerEl" class="ctn-gl relative w-full h-full">
      <canvas
        ref="mainContextEl"
        class="gl-filter absolute top-0 left-0 h-full w-full"
      />

      <canvas
        ref="glSegmentationEl"
        :width="webcamSize.width" :height="webcamSize.height"
        class="gl-segment hidden absolute top-0 left-0 h-full w-full"
      />

      <canvas
        ref="glSegmentationMaskEl"
        :width="webcamSize.width" :height="webcamSize.height"
        class="gl-segment hidden absolute top-0 left-0 h-full w-full"
      />

      <video
        ref="cameraEl"
        :srcObject="stream"
        :width="webcamSize.width"
        :height="webcamSize.height"
        muted
        autoplay
        playsinline
        loop
        :controls="false"
        class="video-stream absolute max-h-50 max-w-60 <sm:max-h-30 <sm:max-w-50 bottom-0 left-0 border border-red object-cover"
      />

      <video
        ref="backgroundLoopEl"
        :src="getAssetURL(dataSrc[0].src)"
        :width="dataSrc[0].size.width"
        :height="dataSrc[0].size.height"
        class="loop absolute h-full w-full object-cover top-0 left-0"
        muted
        autoplay
        playsinline
        loop
        :controls="false"
        preload="metadata"
        type="video/mp4"
      />

      <video
        ref="bubblesLoopEl"
        :src="getAssetURL(dataSrc[1].src)"
        :width="dataSrc[1].size.width"
        :height="dataSrc[1].size.height"
        class="fx absolute h-full w-full object-cover top-0 left-0"
        muted
        playsinline
        loop
        :controls="false"
        preload="metadata"
        type="video/mp4"
      />

      <video
        ref="leafLoopEl"
        :src="getAssetURL(dataSrc[2].src)"
        :width="dataSrc[2].size.width"
        :height="dataSrc[2].size.height"
        class="fx absolute h-full w-full object-cover top-0 left-0"
        muted
        playsinline
        loop
        :controls="false"
        preload="metadata"
        type="video/mp4"
      />

      <span v-if="enabled && isLoading && !snapped" class="body-1 absolute top-1/2 left-1/2 transform -translate-1/2 z-10 whitespace-nowrap text-white">Loading filter...</span>
    </div>
    <slot />
  </section>
</template>

<style lang="scss" scoped>

.ctn-gl {
 z-index: $z-ogl;
}

.video-stream, .loop, .fx {
  z-index: -1;
  background-color: $c-black;
}

</style>
